<template>
    <div class="limiter-content pa-3 pa-lg-0">
        <div class="padding-block"></div>

        <!-- Cabecalho no padrao das telas da area do credor/procurador -->
        <header class="pa-header">
            <span class="pa-eyebrow">
                <v-icon small color="primary">{{ isProcurator ? "mdi-briefcase-account-outline" : "mdi-account-circle-outline" }}</v-icon>
                {{ isProcurator ? "ÁREA DO PROCURADOR" : "ÁREA DO CREDOR" }}
            </span>
            <h1 class="pa-title">Adesão <span>ao plano</span></h1>
            <p class="pa-subtitle">
                Leia o plano de recuperação judicial, escolha a opção de pagamento da sua classe e assine o termo de adesão pelo GOV.br.
                Cada crédito tem o seu próprio termo.
            </p>
        </header>

        <login-required-notice v-if="!$root.user" />

        <template v-else>
        <v-progress-linear v-if="loading" indeterminate color="darkSecondary" class="mb-6"></v-progress-linear>

        <div v-else-if="!campaigns.length" class="pa-empty">
            <div class="pa-empty__icon"><v-icon size="34" color="darkSecondary">mdi-file-sign</v-icon></div>
            <div class="pa-empty__title">Nenhum plano aberto para adesão</div>
            <div class="pa-empty__text">
                No momento não há plano de recuperação judicial aberto para adesão com crédito seu (ou de credor que você representa) no Quadro-Geral de Credores do caso.
            </div>
        </div>

        <!-- Planos abertos (um por caso) -->
        <section v-for="campaign in campaigns" :key="campaign.id" class="pa-card">
            <div class="pa-card__head">
                <div class="pa-card__icon"><v-icon color="darkSecondary">mdi-scale-balance</v-icon></div>
                <div class="pa-card__main">
                    <div class="pa-card__title">{{ campaign.client_name }}</div>
                    <div class="pa-muted">Processo {{ campaign.process_number || "—" }}</div>
                </div>
                <v-btn text rounded color="darkSecondary" class="pa-action" :loading="openingPlan === campaign.id" @click="openPlan(campaign)">
                    <v-icon left small>mdi-file-document-outline</v-icon> Ler o plano<span v-if="campaign.plan">&nbsp;(versão {{ campaign.plan.version }})</span>
                </v-btn>
            </div>

            <div class="pa-notice" :class="campaign.accepting_terms ? 'pa-notice--ok' : 'pa-notice--warn'">
                <v-icon small :color="campaign.accepting_terms ? '#1e7b4f' : '#a16207'">{{ campaign.accepting_terms ? "mdi-check-circle-outline" : "mdi-clock-alert-outline" }}</v-icon>
                <span v-if="campaign.accepting_terms">Adesão aberta{{ campaign.closes_at ? ` até ${formatDateTime(campaign.closes_at)}` : "" }}.</span>
                <span v-else>Adesão {{ campaign.status === "closed" ? "encerrada" : "fora do período" }}. Você ainda pode acompanhar os termos já enviados.</span>
            </div>

            <div class="pa-list-head">
                <h2>Créditos</h2>
                <span>{{ campaign.credits.length }} {{ campaign.credits.length === 1 ? "crédito" : "créditos" }}</span>
            </div>

            <article v-for="credit in campaign.credits" :key="credit.user_creditor_id" class="pa-credit">
                <div class="pa-credit__row">
                    <div class="pa-credit__info">
                        <div v-if="credit.representing" class="pa-credit__represented">
                            <v-icon x-small color="#7e55ab">mdi-account-arrow-right-outline</v-icon>
                            Representado: <span class="pa-capitalize">{{ credit.creditor_name }}</span> ({{ credit.creditor_registration }})
                        </div>
                        <div class="pa-credit__class">{{ credit.class_name }}</div>
                        <div class="pa-muted">Devedora {{ credit.debtor }}</div>
                    </div>
                    <div class="pa-credit__amount">{{ credit.amount_formatted }}</div>
                    <div class="pa-credit__status">
                        <template v-if="credit.current_term">
                            <span class="pa-pill" :class="`pa-pill--${statusTone(credit.current_term.status)}`">
                                <span class="pa-pill__dot"></span>{{ statusOf(credit.current_term).label }}
                            </span>
                            <div class="pa-muted mt-1">Termo {{ credit.current_term.document_code }}</div>
                        </template>
                        <span v-else class="pa-pill" :class="credit.needs_new_signature ? 'pa-pill--warn' : 'pa-pill--muted'">
                            <span class="pa-pill__dot"></span>{{ credit.needs_new_signature ? "Assinar a nova versão do termo" : "Sem adesão" }}
                        </span>
                    </div>
                    <div class="pa-credit__actions">
                        <v-btn v-if="actionFor(campaign, credit)" color="primary" depressed class="pa-btn" :disabled="!!credit.profile && !credit.profile.complete"
                            @click="startWizard(campaign, credit)">
                            {{ actionFor(campaign, credit) }}
                        </v-btn>
                    </div>
                </div>
                <div v-if="credit.current_term && credit.current_term.status === 'rejected'" class="pa-reason">
                    <v-icon x-small color="error">mdi-alert-circle-outline</v-icon> {{ credit.current_term.rejection_reason }}
                </div>
                <div v-if="credit.profile && !credit.profile.complete && actionFor(campaign, credit)" class="pa-pending">
                    <div>
                        <strong>Para aderir, resolva as pendências do cadastro{{ credit.representing ? " do credor representado" : "" }}:</strong>
                        <div class="pa-pending__items">
                            <span v-for="item in credit.profile.missing" :key="item.key" class="pa-pending__item">{{ item.label }}</span>
                        </div>
                    </div>
                    <router-link v-if="!credit.representing" to="/minha-conta" class="pa-link">Ir para Minha conta <v-icon x-small color="#7e55ab">mdi-arrow-right</v-icon></router-link>
                    <router-link v-else-if="credit.association_id" :to="`/procurador/credores/${credit.association_id}/cadastro`" class="pa-link">
                        Ir para o cadastro do credor <v-icon x-small color="#7e55ab">mdi-arrow-right</v-icon>
                    </router-link>
                </div>
            </article>
        </section>

        <!-- Termos emitidos -->
        <section class="pa-terms">
            <div class="pa-list-head">
                <h2>Meus termos</h2>
                <span v-if="terms.length">{{ terms.length }} {{ terms.length === 1 ? "termo" : "termos" }}</span>
            </div>
            <v-progress-linear v-if="loadingTerms" indeterminate color="darkSecondary" class="mb-3"></v-progress-linear>
            <div v-else-if="!terms.length" class="pa-soft">Você ainda não emitiu termos de adesão.</div>
            <article v-for="item in terms" :key="item.id" class="pa-term">
                <div class="pa-term__icon"><v-icon small>mdi-file-sign</v-icon></div>
                <div class="pa-term__main">
                    <div class="pa-term__code">{{ item.document_code }}</div>
                    <div class="pa-muted">{{ item.client_name }}</div>
                    <div class="pa-term__meta">
                        <span>{{ item.credit_snapshot.class_name }} · {{ formatMoney(item.credit_snapshot.amount, item.credit_snapshot.currency) }}</span>
                        <span v-if="item.credit_snapshot.name" class="pa-capitalize">{{ item.credit_snapshot.name }}</span>
                    </div>
                    <div class="pa-term__meta">
                        <span>{{ item.payment_option_title }} · termo v{{ item.template_version }}</span>
                    </div>
                </div>
                <div class="pa-term__status">
                    <span class="pa-pill" :class="`pa-pill--${statusTone(item.status)}`"><span class="pa-pill__dot"></span>{{ statusOf(item).label }}</span>
                    <div v-if="!item.is_current_version && ['submitted', 'in_review', 'approved'].includes(item.status)" class="pa-term__note pa-term__note--warn">
                        Versão anterior: não conta mais para a adesão.
                    </div>
                    <div v-if="item.status === 'rejected' && item.rejection_reason" class="pa-term__note pa-term__note--danger">{{ item.rejection_reason }}</div>
                    <div v-if="item.status === 'revoked' && item.revoke_reason" class="pa-term__note">{{ item.revoke_reason }}</div>
                    <div v-if="item.status === 'cancelled' && item.cancel_reason" class="pa-term__note">{{ cancelReasons[item.cancel_reason] || item.cancel_reason }}</div>
                    <div class="pa-muted mt-1">
                        Emitido {{ formatDateTime(item.generated_at) }}<span v-if="item.submitted_at"> · enviado {{ formatDateTime(item.submitted_at) }}</span>
                    </div>
                </div>
                <div class="pa-term__files">
                    <v-btn text rounded small color="darkSecondary" class="pa-action" @click="downloadTerm(item, 'generated')">
                        <v-icon left small>mdi-download</v-icon> Termo emitido
                    </v-btn>
                    <v-btn v-if="item.submitted_at" text rounded small color="darkSecondary" class="pa-action" @click="downloadTerm(item, 'submitted')">
                        <v-icon left small>mdi-download</v-icon> Termo enviado
                    </v-btn>
                </div>
            </article>
        </section>

        </template>

        <plan-adhesion-wizard v-model="wizard.open" :campaign="wizard.campaign" :credit="wizard.credit" :resume-term="wizard.resumeTerm" @changed="load" />
    </div>
</template>


<script>
import axios from "axios";
import PlanAdhesionWizard from "@/components/plan-adhesion/PlanAdhesionWizard.vue";
import { TERM_STATUS, CANCEL_REASONS, errorMessage, formatDateTime, formatMoney } from "@/components/plan-adhesion/plan-adhesion.util";

export default {
    name: "PlanAdhesionPage",

    components: { PlanAdhesionWizard },

    data: () => ({
        campaigns: [],
        terms: [],
        loading: true,
        loadingTerms: true,
        openingPlan: null,
        cancelReasons: CANCEL_REASONS,
        wizard: { open: false, campaign: null, credit: null, resumeTerm: null },
    }),

    computed: {
        isProcurator() {
            return !!this.$root.user && Number(this.$root.user.type) === 2;
        },
    },

    created() {
        if (this.$root.user) this.load();
    },

    methods: {
        formatDateTime,
        formatMoney,

        // Tom da pilula do status do termo.
        statusTone(status) {
            return {
                awaiting_signature: "warn", submitted: "info", in_review: "info", approved: "ok",
                rejected: "danger", revoked: "muted", cancelled: "muted", draft: "muted",
            }[status] || "muted";
        },

        statusOf(term) {
            return TERM_STATUS[term.status] || { label: term.status_label || term.status, color: "grey" };
        },

        notifyError(error) {
            this.$dialog.notify.error(errorMessage(error), { position: "top-right", timeout: 6000 });
        },

        async load() {
            await Promise.all([this.loadCampaigns(), this.loadTerms()]);
        },

        async loadCampaigns() {
            try {
                const result = await axios.get("/api/plan-adhesions");
                this.campaigns = result.data.content;
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.loading = false;
            }
        },

        async loadTerms() {
            try {
                const result = await axios.get("/api/plan-adhesion-terms");
                this.terms = result.data.content;
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.loadingTerms = false;
            }
        },

        // Rotulo do botao conforme a situacao do credito (null = sem acao).
        actionFor(campaign, credit) {
            if (!campaign.accepting_terms) return null;
            const term = credit.current_term;
            if (!term) return credit.needs_new_signature ? "Assinar nova versão" : "Aderir";
            if (term.status === "awaiting_signature") return "Continuar assinatura";
            if (term.status === "rejected") return "Reenviar termo";
            return null;
        },

        startWizard(campaign, credit) {
            const term = credit.current_term;
            const resume = term && ["awaiting_signature", "rejected"].includes(term.status) ? term : null;
            this.wizard = { open: true, campaign, credit, resumeTerm: resume };
        },

        async openPlan(campaign) {
            const tab = window.open("", "_blank");
            this.openingPlan = campaign.id;
            try {
                const result = await axios.get(`/api/plan-adhesions/${campaign.id}/plan`);
                if (tab) tab.location = result.data.content.url;
                else window.location.assign(result.data.content.url);
                campaign.plan_viewed = true;
            } catch (error) {
                if (tab) tab.close();
                this.notifyError(error);
            } finally {
                this.openingPlan = null;
            }
        },

        async downloadTerm(term, kind) {
            const tab = window.open("", "_blank");
            try {
                const result = await axios.get(`/api/plan-adhesion-terms/${term.id}/file`, { params: { kind } });
                if (tab) tab.location = result.data.content.url;
                else window.location.assign(result.data.content.url);
            } catch (error) {
                if (tab) tab.close();
                this.notifyError(error);
            }
        },
    },
};
</script>

<style scoped>
/* Mesmo padrao visual de "Meus credores", "Minha conta" e "Resumo de casos". */

.pa-header {
    padding: 32px 0 24px;
}

.pa-eyebrow {
    display: flex;
    gap: 8px;
    align-items: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: #766386;
}

.pa-title {
    margin: 14px 0 10px;
    font-size: clamp(28px, 3.4vw, 40px);
    font-weight: 700;
    color: #25113c;
    letter-spacing: -0.035em;
    line-height: 1.2;
}

.pa-title span {
    color: #806697;
}

.pa-subtitle {
    margin: 0;
    max-width: 760px;
    color: #73677c;
    font-size: 14px;
    line-height: 1.6;
}

.pa-muted {
    font-size: 13px;
    color: rgba(24, 0, 50, 0.55);
}

.pa-capitalize {
    text-transform: capitalize;
}

/* Cartao do plano */
.pa-card {
    padding: 20px 22px;
    border-radius: 16px;
    background: #fff;
    border: 1px solid rgba(24, 0, 50, 0.07);
    margin-bottom: 20px;
}

.pa-card__head {
    display: flex;
    align-items: center;
    gap: 12px 16px;
    flex-wrap: wrap;
}

.pa-card__icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(120, 119, 229, 0.14);
    flex-shrink: 0;
}

.pa-card__main {
    flex: 1 1 260px;
    min-width: 0;
}

.pa-card__title {
    font-size: 18px;
    font-weight: 700;
    color: #180032;
    letter-spacing: -0.01em;
}

.pa-notice {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-radius: 12px;
    margin: 16px 0 6px;
    font-size: 14px;
    color: #2a1a40;
}

.pa-notice--ok {
    background: #effaf3;
}

.pa-notice--warn {
    background: #fff7e6;
}

.pa-list-head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin: 18px 0 10px;
}

.pa-list-head h2 {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #7e55ab;
}

.pa-list-head span {
    font-size: 12px;
    color: rgba(24, 0, 50, 0.5);
}

/* Credito */
.pa-credit {
    padding: 16px 18px;
    border-radius: 14px;
    background: #faf9fd;
    margin-bottom: 10px;
}

.pa-credit__row {
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 14px 20px;
}

.pa-credit__represented {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: #7e55ab;
    margin-bottom: 2px;
}

.pa-credit__class {
    font-size: 15px;
    font-weight: 600;
    color: #180032;
}

.pa-credit__amount {
    font-size: 16px;
    font-weight: 700;
    color: #180032;
    white-space: nowrap;
}

.pa-credit__actions {
    display: flex;
    justify-content: flex-end;
}

.pa-reason {
    margin-top: 10px;
    font-size: 13px;
    color: #b42318;
}

.pa-pending {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px 16px;
    flex-wrap: wrap;
    margin-top: 12px;
    padding: 12px 14px;
    border-radius: 12px;
    background: #fff7e6;
    font-size: 13px;
    color: #2a1a40;
}

.pa-pending__items {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}

.pa-pending__item {
    padding: 3px 10px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid rgba(161, 98, 7, 0.25);
    font-size: 12px;
    color: #4a3a5c;
}

.pa-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-weight: 600;
    color: #7e55ab !important;
    text-decoration: none;
    white-space: nowrap;
}

.pa-link:hover {
    text-decoration: underline;
}

/* Botoes */
.pa-btn {
    height: 40px !important;
    padding: 0 20px !important;
    border-radius: 10px;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 600;
}

.pa-action {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 600;
}

/* Pilulas de status */
.pa-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
}

.pa-pill__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
}

.pa-pill--ok {
    background: #e7f6ee;
    color: #1e7b4f;
}

.pa-pill--info {
    background: rgba(120, 119, 229, 0.14);
    color: #4b3a8f;
}

.pa-pill--warn {
    background: #fff4dc;
    color: #a16207;
}

.pa-pill--danger {
    background: #fdecea;
    color: #b42318;
}

.pa-pill--muted {
    background: #f1f0f4;
    color: #6b6475;
}

/* Meus termos */
.pa-terms {
    margin: 8px 0 40px;
}

.pa-soft {
    padding: 18px 20px;
    border-radius: 14px;
    background: rgba(120, 119, 229, 0.07);
    color: #5e5667;
    font-size: 14px;
}

.pa-term {
    display: grid;
    grid-template-columns: auto minmax(0, 1.3fr) minmax(0, 1fr) auto;
    align-items: start;
    gap: 14px 18px;
    padding: 16px 18px;
    border-radius: 16px;
    background: #fff;
    border: 1px solid rgba(24, 0, 50, 0.07);
    margin-bottom: 8px;
}

.pa-term__icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(120, 119, 229, 0.12);
    color: #7e55ab;
}

.pa-term__icon .v-icon {
    color: inherit;
}

.pa-term__code {
    font-size: 15px;
    font-weight: 700;
    color: #180032;
    letter-spacing: 0.01em;
}

.pa-term__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    margin-top: 2px;
    font-size: 13px;
    color: #4a3a5c;
}

.pa-term__note {
    margin-top: 4px;
    font-size: 12px;
    color: #4a3a5c;
}

.pa-term__note--warn {
    color: #a16207;
}

.pa-term__note--danger {
    color: #b42318;
}

.pa-term__files {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}

/* Estado vazio */
.pa-empty {
    text-align: center;
    padding: 40px 16px 56px;
}

.pa-empty__icon {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    margin: 0 auto 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(120, 119, 229, 0.12);
}

.pa-empty__title {
    font-size: 17px;
    font-weight: 600;
    color: #180032;
}

.pa-empty__text {
    margin: 6px auto 0;
    max-width: 560px;
    color: rgba(24, 0, 50, 0.6);
    font-size: 14px;
}

/* Celular */
@media (max-width: 760px) {
    .pa-credit__row {
        grid-template-columns: minmax(0, 1fr) auto;
    }

    .pa-credit__status {
        grid-column: 1 / -1;
    }

    .pa-credit__actions {
        grid-column: 1 / -1;
        justify-content: stretch;
    }

    .pa-credit__actions .pa-btn {
        width: 100%;
    }

    .pa-term {
        grid-template-columns: auto minmax(0, 1fr);
    }

    .pa-term__status,
    .pa-term__files {
        grid-column: 2;
        align-items: flex-start;
    }
}
</style>
